// src/components/managed-sources/GoogleSearchConsoleAuthStep.tsx

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import {
  IconBrandGoogle,
  IconCheck,
  IconExternalLink,
  IconPlugConnected,
  IconShieldCheck,
  IconX,
} from "@tabler/icons-react";

import {
  getGoogleAuthUrl,
  exchangeGoogleToken,
  listGoogleSites,
  testGoogleSearchConsoleConnection,
} from "@/api/managedSources";
import { listAuthorizations } from "@/api/authorizations";
import type { AuthorizationDTO } from "@/types/authorizations";
import {
  authorizationRenderOption,
  authorizationSelectData,
  isUsableByMe,
} from "@/components/authorizations/authorizationOwnership";
import type { GoogleSearchConsoleSite } from "@/types/managedSources";

/**
 * The scope-set key the backend keys its Google OAuth branches on. Both the
 * auth-url and the exchange call must carry it: the consent screen requests
 * `webmasters.readonly` for this key only, and the exchange verifies the grant
 * by listing Search Console sites instead of GA4 properties (a GSC-scoped token
 * cannot list GA4 properties, so the default branch would 400 every connect).
 */
const GSC_CONNECTOR_TYPE = "GOOGLE_SEARCH_CONSOLE";

type Props = {
  authorizationId: string | null;
  /** The VERBATIM Google spelling of the selected property. */
  siteUrl: string;
  onAuthorizationChange: (id: string | null) => void;
  /**
   * Reports the selected property. The value handed up is exactly the string
   * `sites.list` returned — never trimmed, lower-cased or stripped of its
   * `sc-domain:` prefix. Google treats `sc-domain:example.com` and
   * `https://example.com/` as different properties and returns rows under the
   * exact string it gave; the same string becomes the warehouse union
   * discriminator, so a reshaped value produces a source that matches no data.
   */
  onSiteUrlChange: (siteUrl: string) => void;
  /** The "Connection name" the customer typed here, lifted so the wizard can
   *  propose it as the source name instead of overwriting it with a composed
   *  default. Fires on every keystroke; stays empty when an existing
   *  authorization is reused. */
  onConnectionNameChange?: (name: string) => void;
  onValidated: (valid: boolean) => void;
};

/**
 * How a property is labelled in the picker.
 *
 * The full Google spelling is ALWAYS the start of the label — the `sc-domain:`
 * prefix is information, not noise: it is the only thing distinguishing a
 * domain property from a URL-prefix property over the same host, and both can
 * be connected side by side. The suffix names that distinction in words for
 * readers who don't know the prefix.
 */
export function siteOptionLabel(site: GoogleSearchConsoleSite): string {
  const kind = site.site_url.startsWith("sc-domain:")
    ? "Domain property"
    : "URL prefix property";
  return `${site.site_url} — ${kind}`;
}

export default function GoogleSearchConsoleAuthStep({
  authorizationId,
  siteUrl,
  onAuthorizationChange,
  onSiteUrlChange,
  onConnectionNameChange,
  onValidated,
}: Props) {
  // Existing authorizations
  const [existingAuths, setExistingAuths] = useState<AuthorizationDTO[]>([]);
  const [loadingAuths, setLoadingAuths] = useState(true);

  // Mode
  const [mode, setMode] = useState<"existing" | "new">("existing");

  // New auth form
  const [authName, setAuthName] = useState("");
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthError, setOauthError] = useState("");

  // Search Console properties
  const [sites, setSites] = useState<GoogleSearchConsoleSite[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState("");
  // Whether a site list has actually been fetched (or handed over by the token
  // exchange) for the current authorization. Without this, "no sites yet"
  // and "this Google account has no verified sites" are the same empty array,
  // and the empty state would flash before the first request even starts.
  const [sitesLoaded, setSitesLoaded] = useState(false);

  // Test state
  const [testResult, setTestResult] = useState<{
    success: boolean;
    row_count?: number;
    error?: string;
  } | null>(null);
  const [testError, setTestError] = useState("");
  const [testing, setTesting] = useState(false);

  // OAuth popup message listener ref
  const popupRef = useRef<Window | null>(null);
  const exchangedRef = useRef(false);

  // Load existing Google authorizations
  useEffect(() => {
    async function load() {
      setLoadingAuths(true);
      try {
        const auths = await listAuthorizations({ type: "GOOGLE" });
        setExistingAuths(auths);
        // Only rows I may use count (ADR-0329): a list of colleagues'
        // personal connections alone starts on "Create new connection".
        if (!auths.some(isUsableByMe)) {
          setMode("new");
        }
      } catch {
        // Non-fatal
      } finally {
        setLoadingAuths(false);
      }
    }
    load();
  }, []);

  // Validate when authorization + property are set
  useEffect(() => {
    onValidated(!!authorizationId && !!siteUrl);
  }, [authorizationId, siteUrl, onValidated]);

  function resetSiteState() {
    setSites([]);
    setSitesError("");
    setSitesLoaded(false);
    setTestResult(null);
    setTestError("");
  }

  // Listen for OAuth popup messages
  const handleOAuthMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "google_oauth_callback") return;

      const { code, error } = event.data;
      popupRef.current = null;

      if (error) {
        setOauthError(`Google authorization failed: ${error}`);
        setOauthLoading(false);
        return;
      }

      if (!code) {
        setOauthError("No authorization code received from Google.");
        setOauthLoading(false);
        return;
      }

      // Prevent duplicate exchange calls (React Strict Mode / double events)
      if (exchangedRef.current) return;
      exchangedRef.current = true;

      try {
        const result = await exchangeGoogleToken(
          code,
          authName.trim(),
          GSC_CONNECTOR_TYPE,
        );
        onAuthorizationChange(result.authorization_id);
        // The exchange already listed the sites to verify the grant, so this
        // is the authoritative list — no follow-up GET /google/sites needed.
        const granted = result.sites ?? [];
        setSites(granted);
        setSitesLoaded(true);
        if (granted.length === 1) {
          onSiteUrlChange(granted[0].site_url);
        }
      } catch (e: any) {
        setOauthError(e?.message || "Failed to exchange authorization code");
      } finally {
        setOauthLoading(false);
      }
    },
    [authName, onAuthorizationChange, onSiteUrlChange],
  );

  useEffect(() => {
    window.addEventListener("message", handleOAuthMessage);
    return () => window.removeEventListener("message", handleOAuthMessage);
  }, [handleOAuthMessage]);

  async function handleConnectGoogle() {
    if (!authName.trim()) return;
    exchangedRef.current = false;
    setOauthLoading(true);
    setOauthError("");
    resetSiteState();

    try {
      const { url } = await getGoogleAuthUrl(undefined, GSC_CONNECTOR_TYPE);
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      popupRef.current = window.open(
        url,
        "google_oauth",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
      );

      const checkInterval = setInterval(() => {
        if (popupRef.current?.closed) {
          clearInterval(checkInterval);
          if (oauthLoading) {
            setOauthLoading(false);
          }
        }
      }, 1000);
    } catch (e: any) {
      setOauthError(e?.message || "Failed to get authorization URL");
      setOauthLoading(false);
    }
  }

  async function handleSelectExistingAuth(authId: string | null) {
    onAuthorizationChange(authId);
    onSiteUrlChange("");
    resetSiteState();

    if (!authId) return;

    setSitesLoading(true);
    try {
      const result = await listGoogleSites(authId);
      setSites(result.sites);
      setSitesLoaded(true);
      if (result.sites.length === 1) {
        onSiteUrlChange(result.sites[0].site_url);
      }
    } catch (e: any) {
      // The backend already sanitizes this message (the raw Google body stays
      // in its own log line), so it is safe to show as-is — same as the
      // ads-customers step next door.
      setSitesError(
        e?.message || "We couldn't load your Search Console properties.",
      );
    } finally {
      setSitesLoading(false);
    }
  }

  async function handleTest() {
    if (!authorizationId || !siteUrl) return;
    setTesting(true);
    setTestResult(null);
    setTestError("");
    try {
      const result = await testGoogleSearchConsoleConnection(
        authorizationId,
        siteUrl,
      );
      setTestResult(result);
    } catch (e: any) {
      setTestError(e?.message || "Connection test failed");
    } finally {
      setTesting(false);
    }
  }

  const siteSelectData = sites.map((s) => ({
    value: s.site_url,
    label: siteOptionLabel(s),
  }));

  const existingAuthOptions = authorizationSelectData(existingAuths, {
    label: (a) =>
      `${a.name}${a.config?.site_count ? ` (${a.config.site_count} properties)` : ""}`,
  });

  /**
   * Nothing came back, and we know it because we asked.
   *
   * Keyed on `sitesLoaded` rather than on the `authorizationId` prop: the two
   * say the same thing here (the flag is only set by a fetch or an exchange,
   * and `resetSiteState` clears it whenever the connection changes), and the
   * prop is owned by the wizard, so gating on it would make the empty state
   * depend on a parent re-render that has nothing to do with the site list.
   */
  const showEmptyState =
    sitesLoaded && !sitesLoading && !sitesError && sites.length === 0;

  const sitePicker = (
    <>
      {sitesLoading && (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">
            Loading Search Console properties…
          </Text>
        </Group>
      )}

      {sitesError && (
        <Alert color="red" icon={<IconX size={16} />} title="We couldn't load your properties">
          <Stack gap="xs">
            <Text size="sm">{sitesError}</Text>
            <Text size="xs" c="dimmed">
              If this connection was created for another Google service, it may
              not have Search Console access. Create a new connection and
              approve the Search Console permission.
            </Text>
          </Stack>
        </Alert>
      )}

      {showEmptyState && (
        <Alert color="orange" title="No verified properties found">
          No verified sites found for this Google account — verify your site in
          Google Search Console first, or connect a different Google account.
        </Alert>
      )}

      {!sitesLoading && !sitesError && sites.length > 0 && (
        <Select
          label="Search Console property"
          placeholder="Select a property"
          description="One data source per property. Domain and URL-prefix properties for the same site are separate in Google, and stay separate here."
          data={siteSelectData}
          value={siteUrl || null}
          onChange={(val) => {
            // Straight through: the option value IS the verbatim Google
            // spelling. Never normalise it on the way out.
            onSiteUrlChange(val || "");
            setTestResult(null);
            setTestError("");
          }}
          searchable
          size="md"
        />
      )}
    </>
  );

  if (loadingAuths) {
    return (
      <Stack align="center" justify="center" py={60}>
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading authorizations...
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* Hero section */}
      <Group gap="md" wrap="nowrap">
        <ThemeIcon size={48} radius="xl" variant="light" color="blue">
          <IconBrandGoogle size={26} />
        </ThemeIcon>
        <Stack gap={2}>
          <Text fw={700} size="lg">
            Connect Google Search Console
          </Text>
          <Text size="sm" c="dimmed">
            Link a Search Console property to sync organic-search performance —
            clicks, impressions, CTR and position.
          </Text>
        </Stack>
      </Group>

      {/* Mode selector */}
      {existingAuths.length > 0 && (
        <SegmentedControl
          value={mode}
          onChange={(v) => {
            setMode(v as "existing" | "new");
            if (v === "new") {
              onAuthorizationChange(null);
              onSiteUrlChange("");
              resetSiteState();
            }
          }}
          data={[
            { label: "Use existing connection", value: "existing" },
            { label: "Create new connection", value: "new" },
          ]}
          fullWidth
        />
      )}

      {/* Existing authorization */}
      {mode === "existing" && existingAuths.length > 0 && (
        <Stack gap="md">
          <Select
            label="Google authorization"
            placeholder="Select a saved connection"
            data={existingAuthOptions}
            renderOption={authorizationRenderOption(existingAuths)}
            value={authorizationId}
            onChange={handleSelectExistingAuth}
            size="md"
          />

          {sitePicker}
        </Stack>
      )}

      {/* New authorization */}
      {mode === "new" && (
        <Stack gap="md">
          <TextInput
            label="Connection name"
            placeholder="e.g. My Search Console"
            value={authName}
            onChange={(e) => {
              setAuthName(e.currentTarget.value);
              onConnectionNameChange?.(e.currentTarget.value);
            }}
            required
            size="md"
            disabled={!!authorizationId}
          />

          {!authorizationId && (
            <Button
              variant="outline"
              leftSection={<IconBrandGoogle size={18} />}
              rightSection={<IconExternalLink size={14} />}
              onClick={handleConnectGoogle}
              loading={oauthLoading}
              disabled={!authName.trim()}
              size="md"
              fullWidth
            >
              Sign in with Google
            </Button>
          )}

          {oauthError && (
            <Alert color="red" icon={<IconX size={16} />}>
              {oauthError}
            </Alert>
          )}

          {authorizationId && sitePicker}
        </Stack>
      )}

      {/* Test connection */}
      {authorizationId && siteUrl && (
        <>
          <Divider />
          <Group gap="sm">
            <Button
              variant="outline"
              onClick={handleTest}
              loading={testing}
              leftSection={<IconShieldCheck size={16} />}
              size="md"
            >
              Test connection
            </Button>
          </Group>
        </>
      )}

      {testError && (
        <Alert color="red" icon={<IconX size={16} />}>
          {testError}
        </Alert>
      )}

      {testResult && (
        <Paper
          withBorder
          p="md"
          radius="md"
          style={{
            borderColor: testResult.success
              ? "var(--mantine-color-green-4)"
              : "var(--mantine-color-red-4)",
            backgroundColor: testResult.success
              ? "var(--mantine-color-green-0)"
              : "var(--mantine-color-red-0)",
          }}
        >
          <Stack gap="xs">
            <Group gap="xs">
              <ThemeIcon
                size={20}
                color={testResult.success ? "green" : "red"}
                variant="filled"
                radius="xl"
              >
                {testResult.success ? <IconCheck size={12} /> : <IconX size={12} />}
              </ThemeIcon>
              <Text size="sm" fw={500}>
                {testResult.success ? "Connection successful" : "Connection failed"}
              </Text>
            </Group>
            {/* Zero rows is NOT a failure: Search Console data lags two to
                three days, so a freshly verified property legitimately has
                nothing in the last week. Say that instead of leaving the
                customer to read "0" as a problem. */}
            {testResult.success && testResult.row_count === 0 && (
              <Text size="xs" c="dimmed" pl={28}>
                No data in the last few days yet — Search Console publishes
                figures a couple of days late, so this is normal for a new
                property.
              </Text>
            )}
          </Stack>
        </Paper>
      )}

      {/* Saved confirmation */}
      {authorizationId && siteUrl && (
        <Paper
          withBorder
          p="md"
          radius="md"
          style={{
            borderColor: "var(--mantine-color-green-4)",
            backgroundColor: "var(--mantine-color-green-0)",
          }}
        >
          <Group gap="sm">
            <ThemeIcon size={32} color="green" variant="light" radius="xl">
              <IconPlugConnected size={16} />
            </ThemeIcon>
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                Connected
              </Text>
              {/* The full Google spelling, never a shortened one. */}
              <Text size="xs" c="dimmed">
                Property: {siteUrl}
              </Text>
            </Stack>
          </Group>
        </Paper>
      )}
    </Stack>
  );
}
